import { storage, ref, uploadBytes, getDownloadURL, deleteObject } from '@trip-planner-monorepo/shared/firebase/firebaseInit';
import { eventBridgeService, EVENT_TYPES } from '@trip-planner-monorepo/shared/services/eventService';
import { documentService } from './documentService';
import { aiService } from './aiService';

/**
 * Enhanced document processing with real AI integration
 * Replaces the previous simulated AI processing with actual OpenAI GPT-4 Vision
 */

export const documentProcessingService = {
  /**
   * Check if a file type is text-based
   * @param {string} mimeType - The MIME type to check
   * @returns {boolean} True if the file is text-based
   */
  isTextBasedDocument(mimeType) {
    const textTypes = [
      'text/plain', 'text/csv', 'text/html', 'application/json',
      'application/xml', 'text/xml', 'text/rtf', 'application/rtf'
    ];
    return textTypes.includes(mimeType);
  },

  /**
   * Generate a preview for text-based documents
   * @param {File} file - The text file
   * @returns {Promise<Object>} Preview data object
   */
  async generateTextPreview(file) {
    try {
      const text = await file.text();

      // Truncate long text for preview (first 500 characters)
      const previewText = text.length > 500 ? text.substring(0, 500) + '...' : text;

      return {
        type: 'text',
        content: previewText,
        format: 'text/plain',
        generatedAt: new Date().toISOString(),
        confidence: 1.0,
        metadata: {
          originalFormat: file.type,
          fullLength: text.length,
          truncated: text.length > 500,
          size: file.size
        }
      };
    } catch (error) {
      console.error('Error generating text preview:', error);
      throw new Error(`Text preview generation failed: ${error.message}`);
    }
  },

  /**
   * Generate a preview for PDF files using PDF.js
   * @param {File} file - The PDF file
   * @returns {Promise<Object>} Preview data object
   */
  async generatePDFPreview(file) {
    try {
      // Load PDF.js from CDN if not already loaded
      if (typeof window.pdfjsLib === 'undefined') {
        await this.loadPDFJS();
      }

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      // Render first page as preview
      const page = await pdf.getPage(1);

      // Calculate scale to fit within max dimensions (500x500)
      const MAX_PREVIEW_DIMENSION = 500;
      const viewport = page.getViewport({ scale: 1.0 });
      const scale = Math.min(
        MAX_PREVIEW_DIMENSION / viewport.width,
        MAX_PREVIEW_DIMENSION / viewport.height
      );

      const scaledViewport = page.getViewport({ scale: scale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = scaledViewport.height;
      canvas.width = scaledViewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport
      };

      await page.render(renderContext).promise;

      // Compress to JPEG with 0.7 quality
      const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);

      return {
        type: 'pdf',
        content: compressedDataUrl,
        format: 'image/jpeg',
        generatedAt: new Date().toISOString(),
        confidence: 1.0,
        metadata: {
          totalPages: pdf.numPages,
          previewPage: 1,
          size: file.size,
          width: canvas.width,
          height: canvas.height
        }
      };
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      throw new Error(`PDF preview generation failed: ${error.message}`);
    }
  },

  /**
   * Loads PDF.js library from CDN
   * @returns {Promise<void>}
   */
  async loadPDFJS() {
    return new Promise((resolve, reject) => {
      if (typeof window.pdfjsLib !== 'undefined') {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = () => {
        // Set worker path
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load PDF.js library'));
      };
      document.head.appendChild(script);
    });
  },

  /**
   * Generate a preview for image files
   * @param {File} file - The image file
   * @returns {Promise<Object>} Preview data object
   */
  async generateImagePreview(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const dataURL = e.target.result;

          // Create an image to validate and get dimensions
          const img = new Image();
          img.onload = () => {
            // Resize image for preview
            const MAX_PREVIEW_DIMENSION = 500;
            let width = img.width;
            let height = img.height;

            if (width > MAX_PREVIEW_DIMENSION || height > MAX_PREVIEW_DIMENSION) {
              if (width > height) {
                height = Math.round((height * MAX_PREVIEW_DIMENSION) / width);
                width = MAX_PREVIEW_DIMENSION;
              } else {
                width = Math.round((width * MAX_PREVIEW_DIMENSION) / height);
                height = MAX_PREVIEW_DIMENSION;
              }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Compress to JPEG with 0.7 quality
            const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);

            resolve({
              type: 'image',
              content: compressedDataUrl,
              format: 'image/jpeg',
              generatedAt: new Date().toISOString(),
              confidence: 1.0,
              metadata: {
                width: width,
                height: height,
                originalWidth: img.width,
                originalHeight: img.height,
                size: file.size
              }
            });
          };

          img.onerror = () => {
            reject(new Error('Failed to load image for preview'));
          };

          img.src = dataURL;
        } catch (error) {
          reject(new Error(`Preview generation failed: ${error.message}`));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file for preview'));
      };

      reader.readAsDataURL(file);
    });
  },

  /**
   * Upload document to storage and process it with AI
   * Enhanced version with real AI integration
   */
  async processDocument(file, userId, tripId, options = {}) {
    let documentId = null;

    try {
      // 1. Create document with initial metadata structure
      const document = await documentService.createDocument(userId, tripId, file, {
        title: options.title || file.name, // Will be updated by AI if available
        ...options
      });

      documentId = document.id;
      console.log(`Document created with ID: ${documentId}`);

      // 2. Update processing status to 'processing'
      await documentService.updateProcessingStatus(userId, tripId, documentId, 'processing');

      // 3. Publish document upload event
      eventBridgeService.publish(EVENT_TYPES.DOCUMENT_UPLOADED, {
        userId,
        tripId,
        documentId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type
      });

      // 4. Check if AI service is available
      const aiAvailable = await aiService.isAvailable();

      if (aiAvailable) {
        console.log('AI service available, processing document with AI...');

        try {
          // 5. Process document with AI
          const aiResults = await aiService.processDocument(file);

          // 6. Generate preview for images, PDFs, and text documents
          let previewData = null;
          if (file.type.startsWith('image/')) {
            try {
              previewData = await this.generateImagePreview(file);
            } catch (previewError) {
              console.warn('Image preview generation failed:', previewError.message);
            }
          } else if (file.type === 'application/pdf') {
            try {
              previewData = await this.generatePDFPreview(file);
            } catch (previewError) {
              console.warn('PDF preview generation failed:', previewError.message);
            }
          } else if (this.isTextBasedDocument(file.type)) {
            try {
              previewData = await this.generateTextPreview(file);
            } catch (previewError) {
              console.warn('Text preview generation failed:', previewError.message);
            }
          }

          // 7. Update document with AI results and preview
          const updateData = {
            // Use AI-generated title if available, otherwise keep original
            title: aiResults.title || document.title,
            // Keep name field in sync with title for backward compatibility
            name: aiResults.title || document.title,

            // Set categories based on AI categorization
            categories: aiResults.categorization?.categories || [
              { id: 'uncategorized', confidence: 0.5, isPrimary: true }
            ],
            primaryCategory: aiResults.categorization?.primaryCategory || 'uncategorized',

            // Add AI-generated summary if available
            summary: aiResults.summary || null,

            // Store extracted data
            extractedData: aiResults.extractedData || null,

            // Add preview data if generated
            ...(previewData && { preview: previewData }),

            // Update processing status
            processingStatus: 'completed'
          };

          await documentService.updateDocument(userId, tripId, documentId, updateData);

          // 7. Publish document processed event
          eventBridgeService.publish(EVENT_TYPES.DOCUMENT_PROCESSED, {
            userId,
            tripId,
            documentId,
            aiResults: {
              categorization: aiResults.categorization,
              hasTitle: !!aiResults.title,
              hasSummary: !!aiResults.summary,
              hasExtractedData: !!aiResults.extractedData,
              errors: aiResults.errors || []
            }
          });

          console.log(`Document ${documentId} processed successfully with AI`);

          return {
            document: { ...document, ...updateData },
            aiResults: aiResults,
            processingMethod: 'ai'
          };

        } catch (aiError) {
          console.error('AI processing failed, falling back to basic processing:', aiError);

          // Fallback to basic processing without AI
          let previewData = null;
          if (file.type.startsWith('image/')) {
            try {
              previewData = await this.generateImagePreview(file);
              await documentService.updateDocument(userId, tripId, documentId, { preview: previewData });
            } catch (previewError) {
              console.warn('Image preview generation failed in fallback:', previewError.message);
            }
          } else if (file.type === 'application/pdf') {
            try {
              previewData = await this.generatePDFPreview(file);
              await documentService.updateDocument(userId, tripId, documentId, { preview: previewData });
            } catch (previewError) {
              console.warn('PDF preview generation failed in fallback:', previewError.message);
            }
          } else if (this.isTextBasedDocument(file.type)) {
            try {
              previewData = await this.generateTextPreview(file);
              await documentService.updateDocument(userId, tripId, documentId, { preview: previewData });
            } catch (previewError) {
              console.warn('Text preview generation failed in fallback:', previewError.message);
            }
          }

          await documentService.updateProcessingStatus(userId, tripId, documentId, 'completed');

          // Publish processing failed event but still mark document as completed
          eventBridgeService.publish(EVENT_TYPES.DOCUMENT_PROCESSING_FAILED, {
            userId,
            tripId,
            documentId,
            error: aiError.message,
            fallbackUsed: true
          });

          return {
            document: { ...document, ...(previewData && { preview: previewData }) },
            aiResults: null,
            processingMethod: 'fallback',
            error: aiError.message
          };
        }
      } else {
        console.log('AI service not available, using basic processing');

        // 5. Basic processing without AI but with preview generation
        let previewData = null;
        if (file.type.startsWith('image/')) {
          try {
            previewData = await this.generateImagePreview(file);
            await documentService.updateDocument(userId, tripId, documentId, { preview: previewData });
          } catch (previewError) {
            console.warn('Image preview generation failed in basic processing:', previewError.message);
          }
        } else if (file.type === 'application/pdf') {
          try {
            previewData = await this.generatePDFPreview(file);
            await documentService.updateDocument(userId, tripId, documentId, { preview: previewData });
          } catch (previewError) {
            console.warn('PDF preview generation failed in basic processing:', previewError.message);
          }
        } else if (this.isTextBasedDocument(file.type)) {
          try {
            previewData = await this.generateTextPreview(file);
            await documentService.updateDocument(userId, tripId, documentId, { preview: previewData });
          } catch (previewError) {
            console.warn('Text preview generation failed in basic processing:', previewError.message);
          }
        }

        await documentService.updateProcessingStatus(userId, tripId, documentId, 'completed');

        return {
          document: { ...document, ...(previewData && { preview: previewData }) },
          aiResults: null,
          processingMethod: 'basic'
        };
      }

    } catch (error) {
      console.error('Error processing document:', error);

      // Update processing status to failed if we have a document ID
      if (documentId) {
        try {
          await documentService.updateProcessingStatus(
            userId,
            tripId,
            documentId,
            'failed',
            error.message
          );

          // Publish document processing failed event
          eventBridgeService.publish(EVENT_TYPES.DOCUMENT_PROCESSING_FAILED, {
            userId,
            tripId,
            documentId,
            error: error.message,
            fallbackUsed: false
          });
        } catch (updateError) {
          console.error('Failed to update document status after error:', updateError);
        }
      }

      throw error;
    }
  },

  /**
   * Reprocess a document with AI (useful for batch processing existing documents)
   */
  async reprocessDocument(userId, tripId, documentId) {
    try {
      // Get the existing document
      const document = await documentService.getDocument(userId, tripId, documentId);
      if (!document) {
        throw new Error('Document not found');
      }

      // Check if AI service is available
      const aiAvailable = await aiService.isAvailable();
      if (!aiAvailable) {
        throw new Error('AI service not available');
      }

      // Update processing status
      await documentService.updateProcessingStatus(userId, tripId, documentId, 'processing');

      // Download the file for processing (this would need the actual file)
      // For now, we'll skip reprocessing of existing files
      console.warn('Document reprocessing not fully implemented - file content needed');

      // Fallback to completed status
      await documentService.updateProcessingStatus(userId, tripId, documentId, 'completed');

      return {
        success: false,
        message: 'Reprocessing not implemented - file content required'
      };

    } catch (error) {
      console.error('Error reprocessing document:', error);
      await documentService.updateProcessingStatus(userId, tripId, documentId, 'failed', error.message);
      throw error;
    }
  },

  /**
   * Batch process multiple documents with AI
   */
  async batchProcessDocuments(files, userId, tripId, options = {}) {
    const results = [];
    const batchSize = options.batchSize || 5; // Process 5 at a time to avoid rate limits

    for (let i = 0; i < files.length; i += batchSize) {
      const batch = files.slice(i, i + batchSize);

      const batchPromises = batch.map(async (file, index) => {
        try {
          const result = await this.processDocument(file, userId, tripId, {
            ...options,
            batchIndex: i + index
          });
          return { success: true, file: file.name, result };
        } catch (error) {
          console.error(`Failed to process file ${file.name}:`, error);
          return { success: false, file: file.name, error: error.message };
        }
      });

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);

      // Add delay between batches to respect rate limits
      if (i + batchSize < files.length) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
      }
    }

    return {
      total: files.length,
      successful: results.filter(r => r.success).length,
      failed: results.filter(r => !r.success).length,
      results
    };
  },

  /**
   * Get AI service status and configuration
   */
  async getAIStatus() {
    try {
      const isAvailable = await aiService.isAvailable();
      const config = aiService.getConfig();

      return {
        available: isAvailable,
        configuration: config,
        supportedFileTypes: config.supportedFileTypes,
        apiKeyConfigured: config.apiKeyConfigured
      };
    } catch (error) {
      return {
        available: false,
        error: error.message,
        supportedFileTypes: [],
        apiKeyConfigured: false
      };
    }
  },

  // Get document metadata from storage
  async getDocumentMetadata(userId, tripId, documentId) {
    return await documentService.getDocument(userId, tripId, documentId);
  },

  // Get all documents for a trip
  async getDocuments(userId, tripId) {
    return await documentService.fetchDocuments(userId, tripId);
  },

  // Delete a document
  async deleteDocument(userId, tripId, documentId) {
    return await documentService.deleteDocument(userId, tripId, documentId);
  }
};